import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { DateTimeUtil } from '../../utils/DateTimeUtil';
import { AppConstants, SKILL_PROGRESS_ASSESSMENT_CONSTANTS } from '../../constants/AppConstants';
import { AuthorizedDataService } from '../../services/AuthorizedDataService';
import moment from 'moment';
import { BiLinkExternal } from "react-icons/bi";
import classes from './ViewSkillReports.module.css';
import { BsToggleOff, BsToggleOn } from 'react-icons/bs';
import toast from 'react-hot-toast';

export const ViewSkillReports = () => {
    const timeZone = DateTimeUtil.getPreferredTimeZoneForUser();
    const nonOnboardedRef = useRef(null);
    const [rowData, setRowData] = useState([]);
    const [isSmallScreen, setIsSmallScreen] = useState(false);
    const [togglingId, setTogglingId] = useState(null);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 540);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        async function getStudentAssessments() {
            const url = SKILL_PROGRESS_ASSESSMENT_CONSTANTS.getLearnerAssessmentsReports;
            const response = await AuthorizedDataService.getRequest(url, '', '');
            if (response) {
                setRowData(response);
            }
        }
        getStudentAssessments();
    }, []);

    const handleClick = (event, mappingUniqueId) => {
        event.stopPropagation();
        if (mappingUniqueId) {
            window.open(`/learner-assessment/${mappingUniqueId}`, '_blank');
        }
    };

    const updateVisibilityFlag = async (uniqueId, visibilityFlag) => {
        try {
            const url = `${SKILL_PROGRESS_ASSESSMENT_CONSTANTS.updateAssessmentReportVisibilityFlagApi}${uniqueId}${SKILL_PROGRESS_ASSESSMENT_CONSTANTS.AndVisibilityFlag}${visibilityFlag}`;
            const response = await AuthorizedDataService.putRequest(
                url,
                AppConstants.emptyString,
                AppConstants.emptyString
            );

            if (response) {
                toast.success('Visibility updated successfully');
                setRowData((prevData) =>
                    prevData.map((learner) =>
                        learner.mappingUniqueId === uniqueId
                            ? { ...learner, assessmentEnabledFlag: visibilityFlag }
                            : learner
                    )
                );
            } else {
                toast.error('Failed to update visibility');
            }
        } catch (error) {
            console.error('Error updating visibility:', error);
            toast.error('Failed to update visibility');
        }
    };

    const learnerAssessmentsColumnDef = [
        {
            headerName: 'Learner Name',
            field: 'learnerName',
            sortable: true,
            filter: true,
            tooltipValueGetter: (params) =>
                `${params.data.learnerName} (${params.data.learnerQrenciaId})` || AppConstants.notAvailableText,
            cellRendererFramework: (params) => {
                const { learnerName, mappingUniqueId, assessmentType } = params.data;

                const isEnabled = params.data.assessmentEnabledFlag === 'Y' && assessmentType === 'Regular';

                return (
                    <div
                        className={classes.learnerNameSection}
                        onClick={(event) => {
                            if (isEnabled) {
                                event.stopPropagation();
                                handleClick(event, mappingUniqueId);
                            }
                        }}
                        style={{
                            cursor: isEnabled ? 'pointer' : 'default',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        {learnerName}{" "}
                        {isEnabled && < BiLinkExternal />}
                    </div>
                );
            },
        },
        {
            headerName: 'Template Name',
            field: 'templateName',
            sortable: true,
            filter: true,
            minWidth: 265,
            tooltipValueGetter: (params) =>
                `${params.data.templateName}  (${params.data.templateId})` || AppConstants.notAvailableText,
        },
        {
            headerName: 'Assessment Date',
            field: 'assessmentDate',
            filter: 'agTextColumnFilter',
            headerClass: 'multi-line-header',
            minWidth: 150,
            tooltipValueGetter: ({ data }) =>
                data.assessmentDate
                    ? `${moment.utc(data.assessmentDate).tz(timeZone).format('D MMM YYYY')} ${timeZone === AppConstants.utcTimezone ? '(UTC)' : ''}`
                    : AppConstants.notAvailableText,
            valueGetter: ({ data }) => {
                return data.assessmentDate
                    ? `${moment.utc(data.assessmentDate).tz(timeZone).format('D MMM YYYY')} ${timeZone === AppConstants.utcTimezone ? '(UTC)' : ''}`
                    : AppConstants.notAvailableText;
            },
        },
        {
            headerName: 'Assessment Description',
            field: 'assessmentDescription',
            sortable: true,
            filter: true,
            headerClass: 'multi-line-header',
            minWidth: 400,
            valueGetter: (params) =>
                params.data.assessmentDescription || AppConstants.notAvailableText,
            tooltipValueGetter: (params) => {
                const { assessmentDescription, assessmentId } = params.data;
                return assessmentDescription
                    ? `${assessmentDescription} (${assessmentId})`
                    : assessmentId || AppConstants.notAvailableText;
            }
        },
        {
            headerName: 'Score',
            field: 'totalMarks',
            filter: 'agTextColumnFilter',
            headerClass: 'multi-line-header',
            minWidth: 110,
            valueGetter: (params) => {
                const totalMarks =
                    params.data.totalScoreAwarded ?? AppConstants.notAvailableText;
                const maxMarks = params.data.maximumMarks;
                return maxMarks ? `${totalMarks}/${maxMarks}` : `${totalMarks}`;
            },
            tooltipValueGetter: (params) => {
                const totalMarks =
                    params.data.totalScoreAwarded ?? AppConstants.notAvailableText;
                const maxMarks = params.data.maximumMarks;
                return maxMarks ? `${totalMarks}/${maxMarks}` : `${totalMarks}`;
            },
        },
        {
            headerName: 'Actions',
            field: 'actions',
            filter: false,
            headerClass: 'multi-line-header',
            sortable: false,
            minWidth: 120,
            cellRendererFramework: (params) => {
                return (
                    <>
                        {/* Toggle Button */}
                        <Button
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title={
                                params.data.assessmentEnabledFlag === 'Y'
                                    ? 'Restrict'
                                    : 'Enable'
                            }
                            className={`gridIconButton ${classes.gridIcon} ${classes.toggleBtn} ${classes.gridIconMargin}`}
                            variant={
                                params.data.assessmentEnabledFlag === 'Y'
                                    ? 'success'
                                    : 'danger'
                            } // Green for active, Red for inactive
                            disabled={togglingId === params.data.mappingUniqueId}
                            onClick={async () => {
                                const currentVisibility =
                                    params.data.assessmentEnabledFlag;
                                const newVisibility = currentVisibility === 'Y' ? 'N' : 'Y';
                                const uniqueId = params.data.mappingUniqueId;

                                try {
                                    setTogglingId(uniqueId);
                                    await updateVisibilityFlag(uniqueId, newVisibility);
                                } finally {
                                    setTogglingId(null);
                                }
                            }}
                        >
                            {params.data.assessmentEnabledFlag === 'Y' ? (
                                <BsToggleOn size={20} style={{ color: '#28a745' }} />
                            ) : (
                                <BsToggleOff size={20} style={{ color: '#dc3545' }} />
                            )}
                        </Button>

                        <Button
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title="Assessment Report"
                            className={`gridIconButton ${classes.gridIcon} ${classes.reportIcon} ${classes.gridIconMargin}`}
                            disabled={
                                params.data.assessmentEnabledFlag ===
                                AppConstants.falseFlag
                            }
                            onClick={() => {
                                if (params.data.mappingUniqueId) {
                                    window.open(
                                        `/learner-assessment/${params.data.mappingUniqueId}`,
                                        '_blank'
                                    );
                                }
                            }}
                        >
                            <BiLinkExternal />
                        </Button>
                    </>
                );
            },
        },
    ];

    return (
        <div
            className="ag-theme-alpine"
            style={{ width: '100%', height: '100%' }}
        >
            <AgGridReact
                ref={nonOnboardedRef}
                suppressExcelExport={true}
                columnDefs={learnerAssessmentsColumnDef}
                pagination={true}
                paginationPageSize={AppConstants.adminDashboardPaginationSize}
                rowData={rowData}
                enableBrowserTooltips={true}
                domLayout="autoHeight"
                suppressRowHoverHighlight={true}
                defaultColDef={{
                    filter: true,
                    sortable: true,
                    resizable: true,
                    flex: 1,
                    minWidth: 160,
                    suppressMenu: true,
                    suppressHorizontalScroll: true,
                    floatingFilterComponentParams: { suppressFilterButton: true },
                    floatingFilter: true,
                }}
                enableFilter={true}
                enableSorting={true}
                enableSearching={true}
                enableCellTextSelection={true}
            />
        </div>
    );
}
